import { type OperationId, operationIds } from "@gen/app-access-config";
import {
  CoreQuery,
  type IAdminTransaction,
  RefundCustomAmountF2fPaymentBody,
  RefundCustomAmountOnlinePaymentBody,
  RefundFullAmountF2fPaymentBody,
  RefundFullAmountOnlinePaymentBody,
} from "@gen/app-query";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";

import { ActionDialog } from "~/components/action_dialog";
import { TextInput } from "~/components/text_input";
import { createMoneyInputSchema, parseMoney } from "~/entities/money";
import { TransactionMoney } from "~/entities/transaction";
import { config } from "~root/config";

interface RefundActionProps {
  canExecuteForStatus: (operationId: OperationId) => boolean;
  userTransactionRole: "buyer" | "seller";
  isF2f?: boolean;
  transactionData: IAdminTransaction;
}

type FormOutput = {
  refund_amount: number;
};

export function RefundAction({
  canExecuteForStatus,
  userTransactionRole,
  transactionData,
  isF2f,
}: RefundActionProps) {
  const transactionId = transactionData.id;
  const queryClient = useQueryClient();
  const transactionMoney = new TransactionMoney(transactionData);

  const [isDialogOpen, setDialogOpen] = useState(false);

  const onSuccessCommon = () => {
    if (transactionId) {
      queryClient.invalidateQueries({
        queryKey: isF2f
          ? CoreQuery.admin_getF2fTransactionQueryKey(transactionId)
          : CoreQuery.admin_getOnlineTransactionQueryKey(transactionId),
      });
    }
    reset();
  };

  const {
    mutate: refundCustomAmountF2f,
    isPending: isRefundingCustomAmountF2f,
  } = CoreQuery.useAdmin_refundCustomAmountF2fTransactionMutationWithParameters(
    {
      meta: { success: `F2F refund processed for ${userTransactionRole}` },
      onSuccess: onSuccessCommon,
    },
  );

  const {
    mutate: refundCustomAmountOnline,
    isPending: isRefundingCustomAmountOnline,
  } = CoreQuery.useAdmin_refundCustomAmountOnlinePaymentMutationWithParameters({
    meta: { success: `Online refund processed for ${userTransactionRole}` },
    onSuccess: onSuccessCommon,
  });

  const { mutate: refundFullAmountF2f, isPending: isRefundingFullAmountF2f } =
    CoreQuery.useAdmin_refundFullAmountF2fTransactionMutationWithParameters({
      meta: { success: `F2F fully refunded for ${userTransactionRole}` },
      onSuccess: onSuccessCommon,
    });

  const {
    mutate: refundFullAmountOnline,
    isPending: isRefundingFullAmountOnline,
  } = CoreQuery.useAdmin_refundFullAmountOnlinePaymentMutationWithParameters({
    meta: { success: `Online fully refunded for ${userTransactionRole}` },
    onSuccess: onSuccessCommon,
  });

  const locale = navigator.languages?.[0] || config.localization.locale;

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid, errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(
      z.object({
        refund_amount: createMoneyInputSchema(
          transactionMoney.getCurrencyCode(),
          locale,
        ),
      }),
    ),
    defaultValues: { refund_amount: "" },
    mode: "onChange",
  });

  const watchedAmount = watch("refund_amount");
  let currentMoney = transactionMoney.zero();
  if (watchedAmount && !errors.refund_amount) {
    try {
      const integerAmount = parseMoney(
        watchedAmount,
        transactionMoney.getCurrencyCode(),
        locale,
      );
      if (!isNaN(integerAmount)) {
        currentMoney = transactionMoney.create(integerAmount);
      }
    } catch (err) {
      console.error("Invalid amount:", err);
    }
  }

  const onSubmit = (data: FormOutput) => {
    const centAmount = data.refund_amount.toString();

    if (isF2f) {
      refundCustomAmountF2f({
        transaction_id: transactionId,
        refundCustomAmountF2fPaymentBody:
          RefundCustomAmountF2fPaymentBody.fromJS({
            refund_amount: centAmount,
          }),
      });
    } else {
      refundCustomAmountOnline({
        transaction_id: transactionId,
        refundCustomAmountOnlinePaymentBody:
          RefundCustomAmountOnlinePaymentBody.fromJS({
            refund_amount: centAmount,
          }),
      });
    }
  };

  const handleFullRefund = (withFees: boolean) => {
    if (isF2f) {
      refundFullAmountF2f({
        transaction_id: transactionId,
        refundFullAmountF2fPaymentBody: RefundFullAmountF2fPaymentBody.fromJS({
          refund_fees: withFees,
        }),
      });
    } else {
      refundFullAmountOnline({
        transaction_id: transactionId,
        refundFullAmountOnlinePaymentBody:
          RefundFullAmountOnlinePaymentBody.fromJS({
            refund_fees: withFees,
          }),
      });
    }
  };

  const isSubmitting = isF2f
    ? isRefundingCustomAmountF2f || isRefundingFullAmountF2f
    : isRefundingCustomAmountOnline || isRefundingFullAmountOnline;

  if (
    !canExecuteForStatus(
      operationIds["core_admin.refundCustomAmountF2fTransaction"],
    ) &&
    !canExecuteForStatus(
      operationIds["core_admin.refundCustomAmountOnlinePayment"],
    ) &&
    !canExecuteForStatus(
      operationIds["core_admin.refundFullAmountF2fTransaction"],
    ) &&
    !canExecuteForStatus(
      operationIds["core_admin.refundCustomAmountOnlinePayment"],
    )
  ) {
    return null;
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack flexDirection="column" spacing={5}>
          <Typography variant="body1DashedDivider">Custom refund</Typography>

          {canExecuteForStatus(
            isF2f
              ? operationIds["core_admin.refundCustomAmountF2fTransaction"]
              : operationIds["core_admin.refundCustomAmountOnlinePayment"],
          ) && (
            <Controller
              name="refund_amount"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Refund amount"
                  placeholder="Enter amount"
                  data-testid="refund-amount-input"
                  clearable
                  onClear={() => field.onChange("")}
                  fullWidth
                  error={!!errors.refund_amount}
                  helperText={errors.refund_amount?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          {transactionMoney.getCurrencySymbol()}
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          )}

          <Stack flexDirection="column" spacing={3}>
            {canExecuteForStatus(
              isF2f
                ? operationIds["core_admin.refundCustomAmountF2fTransaction"]
                : operationIds["core_admin.refundCustomAmountOnlinePayment"],
            ) && (
              <Button
                variant="contained"
                type="button"
                size="large"
                fullWidth
                disabled={!isDirty || !isValid || isSubmitting}
                onClick={() => setDialogOpen(true)}
                data-testid="confirm-refund-button"
              >
                Confirm refund
              </Button>
            )}
            {canExecuteForStatus(
              isF2f
                ? operationIds["core_admin.refundFullAmountF2fTransaction"]
                : operationIds["core_admin.refundFullAmountOnlinePayment"],
            ) && (
              <>
                <Button
                  variant="text"
                  size="large"
                  fullWidth
                  disabled={isSubmitting}
                  onClick={() => handleFullRefund(true)}
                  data-testid="refund-full-with-fees-button"
                >
                  Full Buyer {isF2f ? "deposit" : ""} refund with fees
                </Button>
                <Button
                  variant="text"
                  size="large"
                  fullWidth
                  disabled={isSubmitting}
                  onClick={() => handleFullRefund(false)}
                  data-testid="refund-full-without-fees-button"
                >
                  Full Buyer {isF2f ? "deposit" : ""} refund without fees
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </form>

      {isDialogOpen && (
        <ActionDialog
          heading="Custom refund"
          message="Are you sure you want to proceed with this custom refund?"
          confirmButtonMessage="Yes, confirm refund"
          cancelButtonMessage="Cancel"
          isConfirming={isSubmitting}
          confirm={handleSubmit(onSubmit)}
          cancel={() => setDialogOpen(false)}
          dataTestId="custom-refund"
        >
          <Stack
            flexDirection="column"
            sx={{
              p: 3,
              border: "1px solid",
              borderColor: "gray.200",
              width: "100%",
              borderRadius: 2,
            }}
            spacing={1}
          >
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="body2">Custom refund</Typography>
              <Typography color="error.main">
                {currentMoney.negate().format()}
              </Typography>
            </Stack>
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="body2">Kept by Trustap</Typography>
              <Typography fontWeight="bold">
                {transactionMoney
                  .getBuyerPaid()
                  .subtract(currentMoney)
                  .format()}
              </Typography>
            </Stack>
          </Stack>
        </ActionDialog>
      )}
    </>
  );
}
